/**
 * Story 10.6 — o NÚCLEO PURO do formulário de candidatura a afiliado: a definição servida pelo Engine e
 * a validação espelhada. Sem React, sem rede, sem DOM.
 *
 * Porque é puro: o harness do storefront é `vitest` com `environment: node` e `include:
 * ["src/**\/*.test.ts"]` — **só `.ts`**. Não existe jsdom, RTL nem Playwright neste repo, logo JSX é
 * INTESTÁVEL aqui. Toda a regra do formulário vive por isso neste módulo, e o componente fica a ser só
 * a renderização dela. Precedente: `modules/bonus/lib/bonus-form-model.ts` (8.1), `lib/util/fiscal.ts`.
 *
 * ESTA VALIDAÇÃO É UX, NÃO É A LEI. A autoridade é o Engine
 * (`apps/backend/src/modules/affiliate/lib/affiliate-application.ts`): o servidor revalida tudo o que
 * chega e é ele que decide. O que está aqui existe para o candidato ver o erro no campo certo antes de
 * esperar por um round-trip.
 *
 * O QUE VEM DA API vs. O QUE ESTÁ COPIADO À MÃO — a distinção é o que impede uma falsa sensação de
 * fonte única:
 *   · VEM de `GET /store/affiliate-application-form`, por campo: `name`, `label`, `type`, `required`,
 *     `maxLength`, `options`, `helpText` e `autoComplete`. Mudar a definição no Engine muda o ecrã E as
 *     duas validações sem tocar em código.
 *   · ESTÁ COPIADO À MÃO deste lado: a união de tipos, o regex de e-mail e TODAS as mensagens de erro.
 *     É outro pacote (não há import possível do backend) e NADA testa que os dois concordam.
 *
 * Por isso o modo de FALHAR é que tem de ser seguro, e é — mas só para as REGRAS (regex, mensagens): o
 * Engine revalida e recusa com a SUA mensagem PT-BR, que a rota devolve também na chave `message` (a
 * única que o `@medusajs/js-sdk` mostra). Uma regra que divirja aqui degrada para o erro do servidor —
 * nunca para um "válido" no browser que o servidor recusa em silêncio.
 *
 * A UNIÃO DE TIPOS não partilha esse modo de falhar, e é honesto dizê-lo: `parseApplicationFormFields`
 * é tudo-ou-nada, logo um `type` que o Engine sirva e esta união não conheça derruba a página INTEIRA
 * para o erro de carregamento — antes de haver submissão. É deliberado e é o lado seguro: renderizar só
 * os campos reconhecidos esconderia um campo que o Engine EXIGE, e aí nenhuma submissão passaria, com o
 * candidato a ler um erro sobre um campo que não está no ecrã.
 *
 * PORQUE NÃO IMPORTA O MODELO DO BÓNUS, que é ponto por ponto o mesmo: são dois contratos com dois
 * endpoints distintos, e o do Bónus já é configurável pelo admin (10.4) enquanto este não. Fundi-los
 * hoje amarrava a candidatura a mudanças de um formulário que não é o dela. É a 2.ª ocorrência do
 * padrão; pela regra de 3 duplica-se agora — o que JÁ foi extraído é o renderizador de campo
 * (`modules/common/components/form-field`), que era a 3.ª.
 */

/** Espelha `AFFILIATE_APPLICATION_FIELD_TYPES` do Engine. Duplicado por ser outro pacote. */
export const APPLICATION_FORM_FIELD_TYPES = [
  "text",
  "email",
  "tel",
  "select",
  "textarea",
] as const

export type ApplicationFormFieldType = (typeof APPLICATION_FORM_FIELD_TYPES)[number]

/** Espelha `AffiliateApplicationField` do Engine. É o contrato de `GET /store/affiliate-application-form`. */
export type ApplicationFormField = {
  name: string
  label: string
  type: ApplicationFormFieldType
  required: boolean
  maxLength: number
  options?: readonly string[]
  helpText?: string
  // Vem da definição (nunca adivinhado do `type`): o `type` diz como o campo se comporta, não o que
  // significa — e é o significado que decide o autofill.
  autoComplete?: string
}

/** Valores do formulário: `name` do campo → o texto tal como o candidato o escreveu. */
export type ApplicationFormValues = Record<string, string>

/** Erros: `name` do campo → mensagem PT-BR. Um campo ausente do mapa está válido. */
export type ApplicationFormErrors = Record<string, string>

// Espelha `AFFILIATE_APPLICATION_EMAIL_PATTERN` do Engine — MESMA regra, deliberadamente permissiva. Se
// divergir, o servidor ganha (e o candidato vê um erro que o browser não previu, que é o modo de falhar
// certo: nunca o contrário).
const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/

const isApplicationFormFieldType = (value: unknown): value is ApplicationFormFieldType =>
  typeof value === "string" && (APPLICATION_FORM_FIELD_TYPES as readonly string[]).includes(value)

const isApplicationFormField = (value: unknown): value is ApplicationFormField => {
  if (typeof value !== "object" || value === null) {
    return false
  }
  const field = value as Record<string, unknown>
  const optionsOk =
    field.options === undefined ||
    (Array.isArray(field.options) && field.options.every((option) => typeof option === "string"))
  return (
    typeof field.name === "string" &&
    typeof field.label === "string" &&
    isApplicationFormFieldType(field.type) &&
    typeof field.required === "boolean" &&
    typeof field.maxLength === "number" &&
    optionsOk &&
    (field.helpText === undefined || typeof field.helpText === "string") &&
    (field.autoComplete === undefined || typeof field.autoComplete === "string")
  )
}

/**
 * Estreita a resposta crua de `GET /store/affiliate-application-form` para a definição. Devolve `null`
 * se o payload não casa o contrato — a página degrada para um erro honesto em vez de renderizar campos
 * meio-formados. Um `any` aqui trocaria uma falha visível por um crash no browser do candidato.
 */
export const parseApplicationFormFields = (data: unknown): ApplicationFormField[] | null => {
  if (typeof data !== "object" || data === null) {
    return null
  }
  const fields = (data as { fields?: unknown }).fields
  // LISTA VAZIA é payload INVÁLIDO, não um formulário sem campos: `[].every(...)` é `true`, logo
  // `{fields: []}` passava e a página renderizava cabeçalho, aviso de privacidade e o botão "Enviar
  // candidatura" — que submetia `{}` e trazia do Engine um 400 "Informe Nome." sobre um campo que não
  // está no ecrã. É exatamente o modo de falhar que o bloco no topo deste ficheiro diz estar a evitar.
  if (!Array.isArray(fields) || fields.length === 0 || !fields.every(isApplicationFormField)) {
    return null
  }
  return fields
}

/** Estado inicial: uma chave vazia por campo da definição (o form é controlado). */
export const emptyApplicationFormValues = (
  fields: readonly ApplicationFormField[],
): ApplicationFormValues => Object.fromEntries(fields.map((field) => [field.name, ""]))

/**
 * Valida UM campo (usada no `blur` e reutilizada no submit). Devolve a mensagem PT-BR ou `null`.
 * As mensagens são as MESMAS do Engine, de propósito: o candidato não deve ver o texto mudar consoante
 * quem validou.
 */
export const validateApplicationField = (
  field: ApplicationFormField,
  value: string,
): string | null => {
  const trimmed = value.trim()

  if (trimmed === "") {
    // Opcional e vazio é VÁLIDO. (Hoje os cinco campos da produção são obrigatórios; a regra continua
    // genérica sobre a definição, que é o ponto do desenho.)
    return field.required ? `Informe ${field.label}.` : null
  }

  if (trimmed.length > field.maxLength) {
    return `${field.label} deve ter no máximo ${field.maxLength} caracteres.`
  }

  switch (field.type) {
    case "email":
      return EMAIL_PATTERN.test(trimmed) ? null : `${field.label} inválido.`
    case "select":
      // Vocabulário fechado; um select sem `options` não aceita valor nenhum (espelha o Engine).
      return field.options?.includes(trimmed) ? null : `${field.label} inválido.`
    case "text":
    case "tel":
    case "textarea":
      // Sem regra de FORMATO, e isso inclui o `website`: exigir uma URL canónica recusaria
      // `instagram.com/fulano`, que é uma resposta legítima. Só `required` e `maxLength` se aplicam.
      return null
  }
}

/** Valida o formulário inteiro. Devolve só os campos com erro (mapa vazio ⇒ pode submeter). */
export const validateApplicationForm = (
  fields: readonly ApplicationFormField[],
  values: ApplicationFormValues,
): ApplicationFormErrors => {
  const errors: ApplicationFormErrors = {}
  for (const field of fields) {
    const error = validateApplicationField(field, values[field.name] ?? "")
    if (error !== null) {
      errors[field.name] = error
    }
  }
  return errors
}

/**
 * O 1.º campo inválido pela ordem da DEFINIÇÃO (não pela ordem de inserção no mapa de erros) — é onde o
 * foco tem de ancorar depois de um submit falhado, e a ordem da definição é a ordem do ecrã.
 */
export const firstInvalidFieldName = (
  fields: readonly ApplicationFormField[],
  errors: ApplicationFormErrors,
): string | null => fields.find((field) => errors[field.name] !== undefined)?.name ?? null

/**
 * O que se envia ao Engine: valores aparados, com os opcionais vazios OMITIDOS. Espelha exatamente o
 * que o validador do servidor grava — mandar `{"website": ""}` faria o servidor gravar uma chave que o
 * candidato nunca preencheu (e, sendo obrigatório, recusá-la-ia com a mensagem de campo em falta).
 *
 * Aparar é a ÚNICA transformação: o valor chega ao `form_data` como o candidato o escreveu.
 */
export const toApplicationSubmission = (
  fields: readonly ApplicationFormField[],
  values: ApplicationFormValues,
): ApplicationFormValues => {
  const submission: ApplicationFormValues = {}
  for (const field of fields) {
    const trimmed = (values[field.name] ?? "").trim()
    if (trimmed !== "") {
      submission[field.name] = trimmed
    }
  }
  return submission
}
